import { useAuth } from '@/contexts/AuthContext';
import { clearAuthStorage } from '@/utils/tokenUtils';
import { useEffect, useRef } from 'react';

interface ApiAuthFailureDetail {
  endpoint: string;
  responseData: any;
  timestamp: string;
}

/**
 * Hook to handle API authentication failures globally
 * Listens for 401 responses and triggers appropriate re-authentication
 */
export const useApiAuthFailureHandler = () => {
  const auth = useAuth();
  const failureCountRef = useRef(0);
  const lastFailureTimeRef = useRef(0);

  useEffect(() => {
    const handleApiAuthFailure = async (event: CustomEvent<ApiAuthFailureDetail>) => {
      const now = Date.now();
      const timeSinceLastFailure = now - lastFailureTimeRef.current;

      // Reset counter if it's been more than 30 seconds since last failure
      if (timeSinceLastFailure > 30000) {
        failureCountRef.current = 0;
      }

      failureCountRef.current++;
      lastFailureTimeRef.current = now;

      console.warn('🚨 API Authentication failure detected:', {
        endpoint: event.detail.endpoint,
        failureCount: failureCountRef.current,
        timeSinceLastFailure
      });

      // If we've had multiple failures in a short time, force re-authentication
      if (failureCountRef.current >= 3) { // Increased threshold from 2 to 3
        console.error('🚨 Multiple API auth failures detected, forcing re-authentication');

        try {
          // Clear all authentication storage
          clearAuthStorage();

          // Force a fresh sign-in without immediate redirect
          if (auth.isAuthenticated) {
            console.debug('🔄 Forcing fresh authentication after multiple failures');
            // Use a timeout to prevent immediate redirect loops
            setTimeout(() => {
              auth.signIn();
            }, 1000);
          } else {
            // If not authenticated, reload to reset state
            console.debug('🔄 Reloading page to reset authentication state');
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        } catch (error) {
          console.error('Error handling auth failure:', error);
          // Fallback: reload the page to reset everything after delay
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      } else {
        // For first/second failure, try to get a fresh token silently
        console.debug('🔄 Auth failure, attempting silent token refresh');
        try {
          const freshToken = await auth.getAccessToken();
          if (!freshToken) {
            console.warn('❌ Could not get fresh token, may need to sign in');
            // Don't force sign-in immediately, let user continue and handle on next API call
          } else {
            console.debug('✅ Successfully refreshed token after auth failure');
          }
        } catch (error) {
          console.error('❌ Error getting fresh token:', error);
        }
      }
    };

    // Listen for API auth failure events
    window.addEventListener('api-auth-failure', handleApiAuthFailure as EventListener);

    return () => {
      window.removeEventListener('api-auth-failure', handleApiAuthFailure as EventListener);
    };
  }, [auth]);

  return {
    failureCount: failureCountRef.current,
    lastFailureTime: lastFailureTimeRef.current
  };
};

export default useApiAuthFailureHandler;